import React, { useEffect, useState } from 'react';
import { FiLayers, FiPlus, FiTrash2, FiLock, FiMonitor } from 'react-icons/fi';
import { getGroups, createGroup, deleteGroup, blockGroup } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

const Groups = () => {
  const { t } = useLanguage();
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [confirmBlock, setConfirmBlock] = useState<string | null>(null);

  const fetchGroups = async () => {
    try {
      const res = await getGroups();
      setGroups(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createGroup(newGroup);
      setShowModal(false);
      setNewGroup({ name: '', description: '' });
      fetchGroups();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteGroup(id); fetchGroups(); } catch (err) { console.error(err); }
  };

  const handleBlockGroup = async (id: string) => {
    try { await blockGroup(id); setConfirmBlock(null); } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-400">Loading...</div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-white text-3xl font-bold">{t('groups')}</h1>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
          <FiPlus />{t('newGroup')}
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="bg-slate-800 rounded-2xl p-12 text-center">
          <FiLayers className="text-slate-600 text-6xl mx-auto mb-4" />
          <p className="text-slate-400 text-lg">{t('noGroups')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => (
            <div key={group.id} className="bg-slate-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-600 p-2 rounded-xl"><FiLayers className="text-white" /></div>
                <h3 className="text-white font-semibold">{group.name}</h3>
              </div>
              {group.description && <p className="text-slate-400 text-sm mb-4">{group.description}</p>}
              <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
                <FiMonitor /><span>{group.device_count} devices</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setConfirmBlock(group.id)}
                  className="flex-1 flex items-center justify-center gap-2 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white px-3 py-2 rounded-lg transition-colors text-sm">
                  <FiLock />{t('lockAll')}
                </button>
                <button onClick={() => handleDelete(group.id)}
                  className="flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white px-3 py-2 rounded-lg transition-colors">
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-white text-xl font-semibold mb-6">{t('newGroup')}</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-slate-400 text-sm mb-2 block">{t('name')}</label>
                <input type="text" value={newGroup.name} onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                  className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="text-slate-400 text-sm mb-2 block">{t('description')}</label>
                <textarea value={newGroup.description} onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                  className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold">{t('create')}</button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold">{t('cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmBlock && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-white text-xl font-semibold mb-4">{t('lockAll')}?</h3>
            <p className="text-slate-400 mb-6">{t('lockAllDesc')}</p>
            <div className="flex gap-3">
              <button onClick={() => handleBlockGroup(confirmBlock)} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold">{t('lock')}</button>
              <button onClick={() => setConfirmBlock(null)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold">{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups;
